import React, { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { toast } from "react-toastify";
import { Plus, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../hooks/AuthContext";
import axios from "axios";
import { CreateProjectDialog } from "../Models/CreateProjectDialog";


const getTodayDate = () => new Date().toISOString().split("T")[0];
const dateToEpoch = (dateStr) => Math.floor(new Date(dateStr).getTime() / 1000);


const priorityOptions = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
  { label: "Urgent", value: "urgent" },
];


export default function EmployeeTaskCreation() {
  const { user } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;


  const [assignedByIds, setAssignedByIds] = useState({});
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);


  const { register, control, handleSubmit, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      tasks: [
        {
          title: "",
          description: "",
          assignedBy: "",
          assignedTo: user?.employeeProfile?._id || "",
          startDate: getTodayDate(),
          endDate: getTodayDate(),
          status: "",
          priority: "",
        },
      ],
    },
  });
  


  const { fields, append, remove } = useFieldArray({ control, name: "tasks" });


  // Fetch employees
  useEffect(() => {
    let isMounted = true;
    const fetchEmployees = async () => {
      try {
        const res = await api.get("/employees");
        if (isMounted) setEmployees(res.data.data.employees || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchEmployees();
    return () => { isMounted = false; };
  }, []);


  // Fetch projects
  useEffect(() => {
    let isMounted = true;
    const fetchProjects = async () => {
      try {
        const res = await api.get("/projects");
        if (isMounted && res.data?.data) setProjects(res.data.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchProjects();
    return () => { isMounted = false; };
  }, []);


// Form submit
// Form submit - Single task vs Bulk tasks
// Form submit - Single task vs Bulk tasks
const handleFormSubmit = async (data) => {
  try {
    if (!user?._id) return toast.error("User data not found.");
    const token = localStorage.getItem("token");
    if (!token) return toast.error("Token missing.");

    const tasksPayload = data.tasks.map((t) => ({
      title: t.title?.trim(),
      description: t.description?.trim(),
      assignedBy: t.assignedBy,
      assignedTo: user?.employeeProfile?._id,
      startDate: dateToEpoch(t.startDate),
      endDate: dateToEpoch(t.endDate),
      priority: t.priority,
      status: t.status,
      createdBy: user?.employeeProfile?._id,
      project_id: t.project_id,
    }));

    let response;

    // ✅ If only 1 task - use single task endpoint
    if (tasksPayload.length === 1) {
      const payload = tasksPayload[0];
      response = await axios.post(
        `${API_URL}tasks`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.status === 200 || response.status === 201) {
        toast.success("Task created successfully!");
      }
    } 
    // ✅ If multiple tasks - use bulk endpoint
    else {
      const payload = {
        tasks: tasksPayload,
      };
      response = await axios.post(
        `${API_URL}tasks/bulk`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.status === 200 || response.status === 201) {
        toast.success(`${tasksPayload.length} tasks created successfully!`);
      }
    }

    // ✅ Reset form after successful submission
    reset({
      tasks: [
        {
          title: "",
          description: "",
          assignedBy: "",
          assignedTo: user?.employeeProfile?._id || "",
          startDate: getTodayDate(),
          endDate: getTodayDate(),
          status: "",
          priority: "",
          project_id: "",
        },
      ],
    });
    
    // ✅ Reset assignedByIds state
    setAssignedByIds({});
        
  } catch (err) {
    console.error(err);
    toast.error(err.response?.data?.message || "Failed to create tasks.");
  }
};





  // Employee task list
  const employeeId = user?.employeeProfile?._id;
  const isEmployee = user?.role === "employee";


  useEffect(() => {
    if (!isEmployee || !employeeId) return;


    const fetchTasks = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return toast.error("Token missing.");
      } catch (err) {
        console.error(err);
        toast.error("Failed to fetch tasks.");
      }
    };


    fetchTasks();
  }, [isEmployee, employeeId,  API_URL]);



  const handleAddTask = () => {
    const newIndex = fields.length;
    append({
      title: "",
      description: "",
      assignedBy: "",
      startDate: getTodayDate(),
      endDate: getTodayDate(),
      status: "",
      priority: "",
    });
    setAssignedByIds((prev) => ({ ...prev, [newIndex]: "" }));
  };


  return (
    <div>
      <div className="flex justify-between items-center pt-2 pb-5">
        <h1 className="text-xl font-bold mb-2">Create Tasks</h1>
        <button
          onClick={() => setIsDialogOpen(true)}
          className="px-2 py-2 flex gap-1 bg-[#2C4E86] text-white rounded-md hover:bg-[#1f3764]"
        >
          <Plus /> Create Project
        </button>
      </div>


      <form onSubmit={handleSubmit(handleFormSubmit)} className="bg-white rounded-lg shadow-sm border p-6 overflow-x-auto">
        <div className="min-w-[1100px]">
          <div className="flex text-sm font-semibold text-gray-700 border-b pb-2 mb-3">
            <div className="w-[15%]">Title</div>
            <div className="flex-1 min-w-[250px]">Description</div>
            <div className="w-[13%]">Project</div>
            <div className="w-[12%]">Assigned By</div>
            <div className="w-[8%]">Priority</div>
            <div className="w-[10%]">Status</div>
            <div className="w-[10%]">Start Date</div>
            <div className="w-[10%]">End Date</div>
            <div className="w-[5%] text-center">Action</div>
          </div>


          {fields.map((field, index) => (
            <div key={field.id} className="flex items-start gap-3 border-b border-gray-100 pb-3 mb-3">
              <div className="w-[15%]">
                <input
                  {...register(`tasks.${index}.title`, { required: "Title is required" })}
                  placeholder="Task title"
                  className="w-full p-2 border rounded-md"
                />
                {errors?.tasks?.[index]?.title && <p className="text-xs text-red-500 mt-1">{errors.tasks[index].title.message}</p>}
              </div>


              <div className="flex-1 min-w-[250px]">
                <textarea
                  {...register(`tasks.${index}.description`, { required: "Description is required" })}
                  placeholder="Task description"
                  className="w-full p-2 border rounded-md h-[40px]"
                />
                {errors?.tasks?.[index]?.description && <p className="text-xs text-red-500 mt-1">{errors.tasks[index].description.message}</p>}
              </div>


              <div className="w-[13%]">
                <select
                  {...register(`tasks.${index}.project_id`, { required: "Project is required" })}
                  className="w-full p-2 border rounded"
                >
                  <option value="">Select Project</option>
                  {projects.map((proj) => (
                    <option key={proj._id} value={proj._id}>{proj.title}</option>
                  ))}
                </select>
                {errors?.tasks?.[index]?.project_id && (
                  <p className="text-red-500 text-xs mt-1">{errors.tasks[index].project_id.message}</p>
                )}
              </div>


              <div className="w-[12%]">
                <select
                  {...register(`tasks.${index}.assignedBy`, { required: "Assigned By is required" })}
                  className="w-full p-2 border rounded-md"
                  value={assignedByIds[index] || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setValue(`tasks.${index}.assignedBy`, val);
                    setAssignedByIds((prev) => ({ ...prev, [index]: val }));
                  }}
                >
                  <option value="">Select</option>
                  {employees.map((emp) => <option key={emp._id} value={emp._id}>{emp.name}</option>)}
                </select>
              </div>


              <div className="w-[8%]">
                <select {...register(`tasks.${index}.priority`, { required: "Priority is required" })} className="w-full p-2 border rounded-md">
                  <option value="">Select</option>
                  {priorityOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>


              <div className="w-[10%]">
                <select {...register(`tasks.${index}.status`, { required: "Status is required" })} className="w-full p-2 border rounded-md">
                  <option value="">Select</option>
                  <option value="to-do">To-Do</option>
                  <option value="pending">Pending</option>
                  <option value="hold">Hold</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>


              <div className="w-[10%]">
                <input type="date" {...register(`tasks.${index}.startDate`, { required: "Start Date is required" })} className="w-full p-2 border rounded-md" min={getTodayDate()} />
              </div>


              <div className="w-[10%]">
                <input type="date" {...register(`tasks.${index}.endDate`, { required: "End Date is required" })} className="w-full p-2 border rounded-md" min={getTodayDate()} />
              </div>


              <div className="w-[5%] flex justify-center items-center">
                <button type="button" onClick={() => remove(index)} className="text-red-500 hover:text-red-700">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>


        <div className="flex justify-end items-center mt-6 pt-4 border-t gap-3">
          <button type="button" onClick={handleAddTask} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md">
            + Add Task
          </button>
          <button type="submit" className="px-6 py-2 text-sm font-medium text-white bg-[#2C4E86] rounded-md">
            Submit {fields.length} Task(s)
          </button>
        </div>
      </form>


      <CreateProjectDialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
    </div>
  );
}
